import * as React from 'react';
// mui
import {
  IconButton,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  DialogActions,
  useTheme,
  useMediaQuery,
  Tooltip,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
// icons
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import ReplayIcon from '@mui/icons-material/Replay';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import MoreVertIcon from '@mui/icons-material/MoreVert';
// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectRecurringTaskById } from '../../../store/selectors/recuringTaskSelectors';
import { selectTaskById } from '../../../store/selectors/taskSelectors';
import { clearExcuse as clearRecurringTaskExcuse } from '../../../store/slices/recurringTaskSlice';
// dayjs
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// types
import Task from '../../../interfaces/Task.interface';
import RecurringTask from '../../../interfaces/RecurringTask.interface';
// axios
import api from '../../../axios';
import { ClearIcon } from '@mui/x-date-pickers';

interface RecurringTaskItemActionsProps {
  taskId: number;
  parentTaskId: number | null;
  isHovered: boolean;
}
const dayNameMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export default function RecurringTaskItemActions(props: RecurringTaskItemActionsProps) {
  // props
  const recurringTaskId = props.taskId;
  const parentTaskId = props.parentTaskId;
  const isHovered = props.isHovered;

  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  // redux
  const localRecurringTask: RecurringTask = useSelector(selectRecurringTaskById(recurringTaskId)) as RecurringTask;
  const selectParentTask = parentTaskId
    ? selectTaskById(parentTaskId)
    : () => null;
  const parentTaskRaw = useSelector(selectParentTask);
  const localParentTask: Task | null = parentTaskRaw ?? null;

  let isLocalRecurringTaskInactive: boolean = false;
  if (localRecurringTask.interval === 'daily') {
    isLocalRecurringTaskInactive = !localRecurringTask.selectedDays?.includes(dayNameMap[dayjs().day()]) || false;
  }

  // state
  const [ confirmDeleteDialogStatus, setConfirmDeleteDialogStatus ] = React.useState(false);
  const [ confirmDeleteLoading, setConfirmDeleteLoading ] = React.useState(false);
  const [ mobileMenuAnchorEl, setMobileMenuAnchorEl ] = React.useState<null | HTMLElement>(null);

  const handleMobileMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(event.currentTarget);
  };
  
  const handleMobileMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMobileMenuAnchorEl(null);
  };

  const handleLocalCompleteToggle = () => {
    if (localRecurringTask.completed) {
      api.put(`/recurring/uncomplete/${localRecurringTask.id}`)
        .then((res) => {
          console.log(res);
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      api.put(`/recurring/complete/${localRecurringTask.id}`, {
        date: dayjs().startOf('day').utc().toISOString()
      })
        .then((res) => {
          console.log(res);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }

  const handleLocalPermCompleteToggle = () => {
    if (localRecurringTask.permanentlyCompleted) {
      api.put(`/recurring/permuncomplete/${localRecurringTask.id}`)
        .then((res) => {
          console.log(res);
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      api.put(`/recurring/permcomplete/${localRecurringTask.id}`, {
        date: dayjs().startOf('day').utc().toISOString()
      })
        .then((res) => {
          console.log(res);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }

  const handleLocalConfirmDeleteButtonClick = () => {
    setConfirmDeleteLoading(true);

    api.delete(`/recurring/delete/${recurringTaskId}`)
      .then((res) => {
        console.log(res);

        if (res.data.affected) {
          setConfirmDeleteLoading(false);
          setConfirmDeleteDialogStatus(false);
        } else {
          console.error('something went wrong with deleting a task!');
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setConfirmDeleteLoading(false);
      });
  }

  const handleRemoveExcuse = () => {
    api.delete(`/recurring/excuses/remove/recurring/${recurringTaskId}`)
      .then((res) => {
        console.log(res);

        if (res.data.affected) {
          dispatch(clearRecurringTaskExcuse(recurringTaskId));
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  return (
    <React.Fragment>
      {/* conditionally render buttons if user is hovering over task */}
      { (isHovered && !isMobile) &&
        <React.Fragment>
          { (!localRecurringTask.completed && localRecurringTask.excused) &&
            <Tooltip title="Remove Excuse">
              <IconButton
                sx={{ color: 'lightgrey', p: isMobile ? 0 : '' }}
                onClick={(e) => { e.stopPropagation(); handleRemoveExcuse(); }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          }

          {/* complete / uncomplete button */}
          { (!localParentTask?.completed && !localRecurringTask.permanentlyCompleted && !isLocalRecurringTaskInactive) &&
            <Tooltip
              title={ localRecurringTask.completed ? 'Undo Task Completion' : 'Complete Task' }
            >
              <span>
                <IconButton
                  sx={{ color: 'lightgrey', p: isMobile ? 0 : '' }}
                  disabled={ localParentTask ? localParentTask.completed : false }
                  onClick={(e) => { e.stopPropagation(); handleLocalCompleteToggle(); }}
                >
                  { localRecurringTask.completed ?
                    <ReplayIcon />
                  :
                    <DoneIcon
                      color='primary'
                    />
                  }
                </IconButton>
              </span>
            </Tooltip>
          }

          { !localParentTask?.completed &&
            <Tooltip
              title={ localRecurringTask.permanentlyCompleted ? 'Undo Permanent Completion' : 'Permanently Complete' }
            >
              <span>
                <IconButton
                  sx={{ color: 'lightgrey', p: isMobile ? 0 : '' }}
                  disabled={ localParentTask ? localParentTask.completed : false }
                  onClick={(e) => { e.stopPropagation(); handleLocalPermCompleteToggle(); }}
                >
                  { localRecurringTask.permanentlyCompleted ?
                    <ReplayIcon />
                  :
                    <PublishedWithChangesIcon
                      color='primary'
                    />
                  }
                </IconButton>
              </span>
            </Tooltip>
          }

          {/* delete button */}
          <IconButton
            sx={{ color: 'lightgrey' }}
            onClick={(e) => { e.stopPropagation(); setConfirmDeleteDialogStatus(true); }}
          >
            <DeleteIcon />
          </IconButton>
        </React.Fragment>
      }

      { isMobile && (
        <React.Fragment>
          <IconButton
            onClick={handleMobileMenuClick}
          >
            <MoreVertIcon />
          </IconButton>

          <Menu anchorEl={mobileMenuAnchorEl} open={Boolean(mobileMenuAnchorEl)} onClose={handleMobileMenuClose}>
            { (!localRecurringTask.permanentlyCompleted && localRecurringTask.excused) && (
              <MenuItem
                onClick={(e) => {
                  handleMobileMenuClose(e);
                  handleRemoveExcuse();
                }}
              >
                <ListItemIcon>
                  <ClearIcon />
                </ListItemIcon>
                <ListItemText primary="Remove Excuse" />
              </MenuItem>
            )}

            { !localParentTask?.completed && !localRecurringTask.permanentlyCompleted && (
              <MenuItem
                disabled={localParentTask?.completed}
                onClick={(e) => {
                  handleMobileMenuClose(e);
                  handleLocalCompleteToggle();
                }}
              >
                <ListItemIcon>
                  {localRecurringTask.completed ? (
                    <ReplayIcon />
                  ) : (
                    <DoneIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    localRecurringTask.completed ? 'Undo Task Completion' : 'Complete Task'
                  }
                />
              </MenuItem>
            )}

            { !localParentTask?.completed && (
              <MenuItem
                disabled={localParentTask?.completed}
                onClick={(e) => {
                  handleMobileMenuClose(e);
                  handleLocalPermCompleteToggle();
                }}
              >
                <ListItemIcon>
                  {localRecurringTask.permanentlyCompleted ? (
                    <ReplayIcon />
                  ) : (
                    <PublishedWithChangesIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    localRecurringTask.permanentlyCompleted
                      ? 'Undo Permanent Completion'
                      : 'Permanently Complete'
                  }
                />
              </MenuItem>
            )}

            <MenuItem
              onClick={(e) => {
                handleMobileMenuClose(e);
                setConfirmDeleteDialogStatus(true);
              }}
            >
              <ListItemIcon>
                <DeleteIcon />
              </ListItemIcon>
              <ListItemText primary="Delete Task" />
            </MenuItem>
          </Menu>
        </React.Fragment>
      )}

      {/* dialog to confirm task deletion */}
      <Dialog
        open={confirmDeleteDialogStatus}
        onClose={(e: any) => { e.stopPropagation(); setConfirmDeleteDialogStatus(false); }}
      >
        <DialogTitle>
          Delete "{localRecurringTask.title}"?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deleting this task will be permanent!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={(e) => { e.stopPropagation(); setConfirmDeleteDialogStatus(false)}}>Cancel</Button>
          <Button loading={confirmDeleteLoading} onClick={(e) => { e.stopPropagation(); handleLocalConfirmDeleteButtonClick(); }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}